/**
 * Util to check if a value is of a certain type or not.
 *
 * memberOrChecker can by either a (single or list of) property name
 * or a function with the following signature (value: any): boolean
 *
 * Example:
 *
 * ```ts
 * interface IInterface {
 *   member: string;
 *   anotherMember?: string;
 *   yetAnotherMember?: string;
 * }
 *
 * isInstanceOf<IInterface>(value, "member");
 * isInstanceOf<IInterface>(value, ["anotherMember", "yetAnotherMember"]);
 * isInstanceOf<IInterface[]>(someArray, Array.isArray);
 * ```
 *
 * @param value of unkown type
 * @param memberOrChecker key or function to check whether value is instance of T
 * @returns boolean
 */ import { _ as _type_of } from "@swc/helpers/_/_type_of";
var isInstanceOf = function(value, memberOrChecker) {
    if (typeof memberOrChecker === "function") {
        return memberOrChecker(value);
    }
    if (value === null || [
        "boolean",
        "number",
        "undefined"
    ].includes(typeof value === "undefined" ? "undefined" : _type_of(value))) {
        // at this point we cannot use `in` operator so we should return false
        return false;
    }
    if (Array.isArray(memberOrChecker)) {
        return memberOrChecker.some(function(member) {
            return member in value;
        });
    }
    return memberOrChecker in value;
};
/**
 * Util to coerce an unknown value if the value is of a certain type otherwise, returns undefined.
 *
 * memberOrChecker can by either a (single or list of) property name
 * or a function with the following signature (value: any): boolean
 *
 * Example:
 *
 * ```ts
 * interface IInterface {
 *   member: string;
 *   anotherMember?: string;
 *   yetAnotherMember?: string;
 * }
 *
 * // returns value as T or undefined
 * asInstanceOrUndefined<IInterface>(value, "member");
 * asInstanceOrUndefined<IInterface>(value, ["anotherMember", "yetAnotherMember"]);
 * asInstanceOrUndefined<IInterface[]>(someArray, Array.isArray);
 * ```
 *
 * @param value of unkown type
 * @param memberOrChecker key or function to check whether value is instance of T
 * @returns value as T or undefined
 */ export var asInstanceOrUndefined = function(value, memberOrChecker) {
    if (isInstanceOf(value, memberOrChecker)) {
        return value;
    }
    return undefined;
};
export function fulfilledOrStale(observablePromise, memberOrChecker, fallback) {
    var _observablePromise_case;
    return (_observablePromise_case = observablePromise === null || observablePromise === void 0 ? void 0 : observablePromise.case({
        fulfilled: function(response) {
            return response;
        },
        pending: function(stale) {
            return asInstanceOrUndefined(stale, memberOrChecker);
        }
    })) !== null && _observablePromise_case !== void 0 ? _observablePromise_case : fallback;
}
